/**
 * The people at a property, and who of them comes onto a new job there
 * (decision 126).
 *
 * A person at a site (`Contact.siteId`) has a role there: the tenant or the
 * site manager is who we call to get in, the owner owns it, the property
 * manager manages it for the owner. The role is its own field,
 * `Contact.siteRole`, because the free-text position drops imported
 * "Tenant" and "Property Manager" as ServiceM8 leftovers
 * (`positionIsRoleWord`); a person with no role is still read from those
 * words.
 */

export const SITE_ROLES = [
  { key: 'TENANT', label: 'Tenant', hint: 'Lives or works there. Who we call to get in.' },
  { key: 'SITE_MANAGER', label: 'Site manager', hint: 'Runs the place. Who we call to get in.' },
  { key: 'OWNER', label: 'Owner', hint: 'Owns the property.' },
  { key: 'PROPERTY_MANAGER', label: 'Property manager', hint: 'Manages it for the owner.' },
] as const

export type SiteRoleKey = (typeof SITE_ROLES)[number]['key']

export const SITE_ROLE_KEYS = SITE_ROLES.map((role) => role.key) as [SiteRoleKey, ...SiteRoleKey[]]

export const SITE_ROLE_LABEL = Object.fromEntries(SITE_ROLES.map((role) => [role.key, role.label])) as Record<
  SiteRoleKey,
  string
>

const FROM_POSITION: ReadonlyArray<[RegExp, SiteRoleKey]> = [
  [/^(tenants?|occupants?|residents?)$/i, 'TENANT'],
  [/^(property manager|managing agent|agent|pm)$/i, 'PROPERTY_MANAGER'],
  [/^(site manager|centre manager|caretaker|facilities manager)$/i, 'SITE_MANAGER'],
  [/^(owners?|landlords?)$/i, 'OWNER'],
]

/** A person's role at their site: the field, else an imported word for one. */
export function siteRoleFor(person: { siteRole?: string | null; position?: string | null }): SiteRoleKey | null {
  if (person.siteRole && person.siteRole in SITE_ROLE_LABEL) return person.siteRole as SiteRoleKey
  const position = person.position?.trim()
  if (!position) return null
  return FROM_POSITION.find(([pattern]) => pattern.test(position))?.[1] ?? null
}

/** Whether this role is who we call to get in. */
export function letsUsIn(role: SiteRoleKey | null): boolean {
  return role === null || role === 'TENANT' || role === 'SITE_MANAGER'
}

export interface JobPersonPlan {
  contactId: string
  roles: Array<'JOB' | 'SITE' | 'OTHER'>
  label: string | null
  isBilling: boolean
  receivesComms: boolean
}

/**
 * Who comes onto a new job at a site, and as what.
 *
 * Only a site with people changes anything: a job anywhere else starts
 * empty, as it always has, and shows the client's own people until somebody
 * is named. At a site with people:
 * - the tenant or site manager (or anybody there with no role) is Site, the
 *   person we call to get in;
 * - an owner or property manager there is on it, labelled, kept posted;
 * - the client's main person is Job and keeps the invoices, so a tenant is
 *   never billed by default;
 * - under an agency billed c/-, the agency's main person is kept posted,
 *   labelled with the agency's name.
 */
export function planJobPeople(input: {
  /** The client's own main person: primary, else the first. */
  clientPerson: { id: string } | null
  sitePeople: ReadonlyArray<{ id: string; siteRole?: string | null; position?: string | null }>
  /** The agency's main person, when the client is billed c/- it. */
  parentPerson: { id: string; parentName: string } | null
}): JobPersonPlan[] {
  if (input.sitePeople.length === 0) return []

  const plan: JobPersonPlan[] = []
  const add = (row: JobPersonPlan) => {
    if (!plan.some((other) => other.contactId === row.contactId)) plan.push(row)
  }

  if (input.clientPerson) {
    add({ contactId: input.clientPerson.id, roles: ['JOB'], label: null, isBilling: true, receivesComms: true })
  }
  for (const person of input.sitePeople) {
    const role = siteRoleFor(person)
    add(
      letsUsIn(role)
        ? { contactId: person.id, roles: ['SITE'], label: null, isBilling: false, receivesComms: true }
        : {
            contactId: person.id,
            roles: ['OTHER'],
            label: SITE_ROLE_LABEL[role!],
            isBilling: false,
            receivesComms: true,
          },
    )
  }
  if (input.parentPerson) {
    add({
      contactId: input.parentPerson.id,
      roles: ['OTHER'],
      label: input.parentPerson.parentName.slice(0, 60),
      isBilling: false,
      receivesComms: true,
    })
  }
  return plan
}
